import styled, { createGlobalStyle } from 'styled-components';
import React, { useCallback, useEffect, useState } from 'react';
import { PRIVACY_POLICY_STATIC_PAGE_KEY } from '../../api/shopApi';
import { OverlayShownLabel } from 'Interfaces/overlayTracking';
import { Option, OverlayId, OverlayOpenOptions } from 'Types/types';
import { useConfig, useLoginState } from 'AppShell/hooks';
import SignupOverlay from 'Components/SignupOverlay';
import { isShopToClubOverlay } from 'Helpers/customEventHelper';
import useApi from 'Hooks/useApi';
import { useCustomEvent } from 'Hooks/useCustomEvent';
import { useIsMultiStep } from 'Hooks/useIsMultiStep';
import useSignupFormTracking from 'Hooks/useSignupFormTracking';
import { useShopToClubOverlayConfig } from 'Hooks/useShopToClubOverlayConfig';
import { useShouldShowForcedShopToClub } from 'Hooks/useShouldShowForcedShopToClub';
import { SHOP_TO_CLUB_OVERLAY_SHOWN_IN_THIS_SESSION, useShouldShowShopToClub } from 'Hooks/useShouldShowShopToClub';
import useWasOverlayShown from 'Hooks/useWasOverlayShown';
import { AbTestVariant } from 'Interfaces/AbTestVariant';
import { topLevelDomains } from 'Interfaces/topLevelDomains';
import useBellaCookies, { oneDayTTL } from 'Hooks/useBellaCookies';

export const StyledSignupAndLoginMain = styled.main`
    position: fixed;
    width: 100vw;
    top: 0;
    left: 0;
    z-index: 100;
`;

const ScreenBlockerGlobalStyle = createGlobalStyle`
    body {
        overflow: hidden !important;
    }
`;

const SignupAndLoginMain: React.FunctionComponent = (): JSX.Element => {
    const { redirectUrl } = useLoginState();
    const { shopToClubOverlayId } = useConfig();
    const {
        isDismissible: isDismissibleDefault,
        isUndismissibleFromOutsideClick: isUndismissibleFromOutsideClickDefault,
    } = useShopToClubOverlayConfig();
    const [, setHasBeenViewedStatus] = useWasOverlayShown();
    const { expirationInDays } = useShopToClubOverlayConfig();
    const { hideOverlay } = useCustomEvent();
    const [privacyPolicy, setPrivacyPolicy] = useState('');
    const { getCmsStaticPage } = useApi();
    const { trackOverlayShown } = useSignupFormTracking();
    const [, setShopToClubOverlayShownInThisSession] = useBellaCookies(
        SHOP_TO_CLUB_OVERLAY_SHOWN_IN_THIS_SESSION,
        true
    );

    const isMultiStep = useIsMultiStep();

    const [isOpen, setIsOpen] = React.useState(false);
    const [keepForcedClose, setKeepForcedClose] = React.useState(false);
    const [showLoginAtStart, setShowLoginAtStart] = React.useState(false);
    const [isDismissible, setIsDismissible] = React.useState(isDismissibleDefault);
    const [isUndismissibleFromOutsideClick, setIsUndismissibleFromOutsideClick] = React.useState(
        isUndismissibleFromOutsideClickDefault
    );
    const [trackingLabel, setTrackingLabel] = React.useState(OverlayShownLabel.Default);

    const parseAndSetOverlayOptions = (openOptions: OverlayOpenOptions) => {
        const setOptionsArray: Array<Option> = [
            { key: 'isLogin', set: setShowLoginAtStart },
            { key: 'trackingLabel', set: setTrackingLabel },
            { key: 'isDismissible', set: setIsDismissible },
            { key: 'isUndismissibleFromOutsideClick', set: setIsUndismissibleFromOutsideClick },
        ];

        setOptionsArray.forEach((option: Option) => {
            if (openOptions[option.key] !== undefined) {
                option.set(openOptions[option.key]);
            }
        });
    };

    const handleOverlayClose = () => {
        setIsOpen(false);
        setKeepForcedClose(true);
        hideOverlay(shopToClubOverlayId as OverlayId);
    };

    useEffect(() => {
        if (keepForcedClose) {
            setIsOpen(false);
        }
    }, [keepForcedClose]);

    const handleOverlayOpen = useCallback(
        (openOptions: OverlayOpenOptions) => {
            parseAndSetOverlayOptions(openOptions);
            // Bella sends default tracking label as an empty string
            // Have to be converted to 'Default' value or it won't be tracked
            trackOverlayShown(trackingLabel.length ? trackingLabel : OverlayShownLabel.Default);
            setIsOpen(true);
            setShopToClubOverlayShownInThisSession(1, oneDayTTL);
            if (expirationInDays) {
                const cookieTTL = expirationInDays * 24 * 60 * 60 * 1000;
                setHasBeenViewedStatus(1, cookieTTL);
            }
        },
        [
            expirationInDays,
            setHasBeenViewedStatus,
            setShopToClubOverlayShownInThisSession,
            trackOverlayShown,
            trackingLabel,
        ]
    );

    useShouldShowShopToClub(handleOverlayOpen, isOpen);
    useShouldShowForcedShopToClub(handleOverlayOpen, isOpen, keepForcedClose);

    useEffect(() => {
        const getPrivacyPolicy = async () => {
            if (isMultiStep) {
                await getCmsStaticPage(PRIVACY_POLICY_STATIC_PAGE_KEY, setPrivacyPolicy, () => ({}));
            }
        };
        getPrivacyPolicy();
    }, [isMultiStep, getCmsStaticPage]);

    const [isFormReady, setIsFormReady] = React.useState(true);

    useEffect(() => {
        const handleOverlayOpenEvent = (event: CustomEvent) => {
            const { action, options } = event.detail;
            if (isShopToClubOverlay(event.detail)) {
                if (action === 'SHOW') {
                    handleOverlayOpen(options);
                }
            }
        };

        document.addEventListener('UPDATE_OVERLAYS', handleOverlayOpenEvent);
        return () => document.removeEventListener('UPDATE_OVERLAYS', handleOverlayOpenEvent);
    }, [expirationInDays, handleOverlayOpen, isOpen, setHasBeenViewedStatus]);

    return (
        <StyledSignupAndLoginMain data-testid="mfSignupAndLogin">
            {isOpen && <ScreenBlockerGlobalStyle />}
            <SignupOverlay
                formHiddenFields={{
                    formType: { name: 'type', value: 'email_and_password' },
                    pageType: { name: 'pagetype', value: 'pagetype' },
                }}
                isOpen={isOpen}
                handleClose={handleOverlayClose}
                redirectUrl={redirectUrl || ''}
                isClosable={isDismissible && !isUndismissibleFromOutsideClick}
                isMagicLinkEnabled={false}
                showLoginAtStart={showLoginAtStart}
                mailcheckOptions={{
                    domains: ['de-DE'],
                    topLevelDomains,
                }}
                formIdentifiers={{
                    form: {
                        login: 'loginForm',
                        signup: 'signupForm',
                    },
                    email: 'signup-resp__form__input--email',
                    password: 'signup-resp__form__input--password',
                    submit: 'submitButton',
                }}
                isMultiStep={isMultiStep}
                privacyPolicy={privacyPolicy}
                formText=""
                backgroundColor=""
                passwordlessABTestVariant={AbTestVariant.EMPTY}
                forgetPasswordRedirectUrl=""
                setIsFormReady={(value: React.SetStateAction<boolean>) => setIsFormReady(value)}
                isFormReady={isFormReady}
                partialLoginEmail=""
                trackingLabel={trackingLabel}
            />
        </StyledSignupAndLoginMain>
    );
};

export default SignupAndLoginMain;
